import { useRef, useEffect, useState, useCallback } from "react";
import SaveImagePrompt from "./SaveImagePrompt";
import UploadedImages from "./UploadedImages";
import { BsExclamationCircleFill, BsFillCameraFill, BsFillCloudArrowUpFill } from "react-icons/bs";
import SaveMultiplePrompt from "./SaveMultiplePrompt";
import FileSaver from "file-saver";
import ImagesInSQS from "./ImagesInSQS";
import AiImageReviewModal from "../../ai/AiImageReviewModal";

const CameraContainer = ({
	sectionName,
	data,
	setInitialFieldData,
	cameraid,
	api,
	token,
	fieldName,
	projectName,
	templateName,
	stage,
	readOnly,
	templateTypeId,
	setHasPhotos,
	initialThumbnailPathes,
	setLastModifiedOn,
	projectId,
	odooId,
	selectedOdooModel,
	odooInstallationName,
	odooParentName,
	solarGradeData = null,
	inSQS,
	setInSQS,
	startingPendingImages,
	aiReviewPrompt = "",
	fieldValue = "",
	fieldComment = "",
}) => {
	const uploadMulitpleRef = useRef();
	const uploadSingleRef = useRef();
	const thumbnailBoxRef = useRef();
	const windowWidth = useRef(window.innerWidth);
	const [imgSrc, setImgSrc] = useState(null);
	const [uploadedImages, setUploadedImages] = useState([]);
	const [uploadPreviews, setUploadPreviews] = useState([]);
	const [recentlyAddedPaths, setRecentlyAddedPaths] = useState([]);
	const [knownPhotoPaths, setKnownPhotoPaths] = useState([]);
	const [numberOfUploaded, setNumberOfUploaded] = useState(0);
	const [viewedThumbnailList, setViewedThumbnailList] = useState(false);
	const [dragActive, setDragActive] = useState(false);
	const [uploadingImages, setUploadingImages] = useState(false);
	const [uploadErrorMessage, setUploadErrorMessage] = useState("");
	const [showAiReviewModal, setShowAiReviewModal] = useState(false);

	useEffect(() => {
		const initialPaths = (initialThumbnailPathes || []).map((thumbnail) => thumbnail.path_lower).filter(Boolean);
		setKnownPhotoPaths((currentPaths) => [...new Set([...currentPaths, ...initialPaths, ...(recentlyAddedPaths || [])])]);
	}, [initialThumbnailPathes, recentlyAddedPaths]);
	// const [isLoading, setIsLoading] = useState();
	// const [uploadingImages, setUploadingImages] = useState();
	const hydratedRef = useRef(false);

	useEffect(() => {
		if (hydratedRef.current) return;
		if (!startingPendingImages?.length) return;

		const norm = (s) => (s || "").toLowerCase().replaceAll("\\", "/");

		const hasQuerySegment = (dropboxPath, query) => {
			const p = norm(dropboxPath);
			const q = norm(query);
			return p.includes(q);
		};

		hydratedRef.current = true;

		let query = sectionName.replaceAll("/", "-") + " - " + fieldName;
		query = query
			.replaceAll("/", "_")
			.replaceAll("\\", "_")
			.replaceAll("<", "_")
			.replaceAll(">", "_")
			.replaceAll(":", "_")
			.replaceAll('"', "_")
			.replaceAll("|", "_")
			.replaceAll("?", "_")
			.replaceAll("*", "_")
			.replaceAll(".", "_");
		const forThisField = startingPendingImages.filter((x) => hasQuerySegment(x.dropbox_path, query.toLowerCase()));

		setInSQS((prev) => {
			return [
				...prev,
				...forThisField.map((mapData) => {
					return { id: mapData.id };
				}),
			];
		});
	}, [startingPendingImages, setInSQS, sectionName, fieldName]);

	useEffect(() => {
		const observer = new IntersectionObserver((entries) => {
			const entry = entries[0];
			if (viewedThumbnailList === false && entry.isIntersecting) {
				setViewedThumbnailList(true);
			}
		});
		if (thumbnailBoxRef !== null) {
			observer.observe(thumbnailBoxRef.current);
		}
		return () => {
			observer.disconnect();
		};
	}, [viewedThumbnailList]);

	useEffect(() => {
		if (setHasPhotos) {
			if (numberOfUploaded > 0) {
				setHasPhotos(true);
			} else {
				setHasPhotos(false);
			}
		}
	}, [numberOfUploaded, setHasPhotos]);

	useEffect(() => {
		let previews = [];
		for (let i = 0; i < uploadedImages.length; i++) {
			previews.push(URL.createObjectURL(uploadedImages[i]));
		}
		setUploadPreviews(previews);
	}, [uploadedImages]);

	const uploadImage = useCallback(
		async (e, downloadFileName) => {
			if (e) {
				e.preventDefault();
			}
			// setIsLoading(true);
			let tempImgSrc = imgSrc;
			setImgSrc(null);
			const formData = new FormData();
			if (downloadFileName) {
				FileSaver.saveAs(tempImgSrc, downloadFileName);
				await new Promise((r) => setTimeout(r, 100));
			}

			fetch(tempImgSrc)
				.then((res) => {
					// setIsLoading(false);
					if (res) {
						return res.blob();
					}
				})
				.then((resBlob) => {
					if (resBlob) {
						formData.append("newImage", resBlob, "newImage");
						formData.append("name", fieldName);
						formData.append("odoo_name", projectName);
						formData.append("template_type_id", templateTypeId);
						formData.append("image_name", sectionName.replaceAll("/", "-") + " - " + fieldName);
						formData.append("template_name", templateName);
						formData.append("project_id", projectId);
						formData.append("odooId", odooId);
						formData.append("odooModel", selectedOdooModel);
						formData.append("odooInstallationName", odooInstallationName);
						formData.append("odooParentName", odooParentName);
						if (solarGradeData) {
							formData.append("solar_visit_id", solarGradeData.solar_visit_id);
							formData.append("solar_project_id", solarGradeData.solar_project_id);
							formData.append("solar_issue_id", solarGradeData.solar_issue_id);
						}
						fetch(api + "/project/image", {
							method: "POST",
							headers: {
								enctype: "multipart/form-data",
								Authorization: "Bearer " + token,
							},
							body: formData,
							mode: "cors",
						})
							.then((res2) => {
								// setIsLoading(false);
								if (res2) {
									return res2.json();
								}
							})
							.then((res3) => {
								setLastModifiedOn(new Date());
								if (res3 && res3.s3Id) {
									setInSQS((prev) => [...prev, { id: res3.s3Id, img: tempImgSrc }]);
								}
							})
							.catch((err) => {});
					} else {
					}
				})
				.catch((err) => {});
		},
		[
			imgSrc,
			fieldName,
			projectName,
			templateTypeId,
			sectionName,
			templateName,
			projectId,
			odooId,
			selectedOdooModel,
			odooInstallationName,
			odooParentName,
			solarGradeData,
			api,
			token,
			setLastModifiedOn,
			setInSQS,
		]
	);

	const uploadEditedImage = useCallback(
		async (downloadFileName, editedImage) => {
			// setIsLoading(true);
			let tempImgSrc = editedImage;
			setImgSrc(null);
			const formData = new FormData();

			fetch(tempImgSrc)
				.then((res) => {
					// setIsLoading(false);
					if (res) {
						return res.blob();
					}
				})
				.then((resBlob) => {
					if (resBlob) {
						formData.append("newImage", resBlob, "newImage");
						formData.append("name", fieldName);
						formData.append("odoo_name", projectName);
						formData.append("template_type_id", templateTypeId);
						formData.append("image_name", sectionName.replaceAll("/", "-") + " - " + fieldName);
						formData.append("template_name", templateName);
						formData.append("project_id", projectId);
						formData.append("odooId", odooId);
						formData.append("odooModel", selectedOdooModel);
						formData.append("odooInstallationName", odooInstallationName);
						formData.append("odooParentName", odooParentName);
						if (solarGradeData) {
							formData.append("solar_visit_id", solarGradeData.solar_visit_id);
							formData.append("solar_project_id", solarGradeData.solar_project_id);
							formData.append("solar_issue_id", solarGradeData.solar_issue_id);
						}
						fetch(api + "/project/image", {
							method: "POST",
							headers: {
								enctype: "multipart/form-data",
								Authorization: "Bearer " + token,
							},
							body: formData,
							mode: "cors",
						})
							.then((res2) => {
								// setIsLoading(false);
								if (res2) {
									return res2.json();
								}
							})
							.then((res3) => {
								setLastModifiedOn(new Date());

								if (res3 && res3.s3Id) {
									setInSQS((prev) => [...prev, { id: res3.s3Id, img: tempImgSrc }]);
								}
							})
							.catch((err) => {});
					} else {
					}
				})
				.catch((err) => {});
		},
		[
			fieldName,
			projectName,
			templateTypeId,
			sectionName,
			templateName,
			projectId,
			odooId,
			selectedOdooModel,
			odooInstallationName,
			odooParentName,
			solarGradeData,
			api,
			token,
			setLastModifiedOn,
			setInSQS,
		]
	);

	const uploadImages = useCallback(
		async (e) => {
			if (e) {
				e.preventDefault();
			}
			if (uploadPreviews.length <= 0) {
				return;
			}

			const copyOfUploadPreviews = uploadPreviews;
			setUploadPreviews([]);
			setUploadErrorMessage("");

			setUploadingImages(true);
			const failureReasons = [];
			const describeUploadFailure = (message) => {
				if (!message || message === "Could not convert HEIC image") {
					return "an unsupported image type";
				}
				return message;
			};
			try {
				for (let previewIndex = 0; previewIndex < copyOfUploadPreviews.length; previewIndex++) {
					let uploadImage = await fetch(copyOfUploadPreviews[previewIndex]);
					let imageBlob = await uploadImage.blob();
					const formData = new FormData();
					formData.append("newImage", imageBlob, "newImage");
					formData.append("name", fieldName);
					formData.append("odoo_name", projectName);
					formData.append("image_name", sectionName.replaceAll("/", "-") + " - " + fieldName);
					formData.append("template_type_id", templateTypeId);
					formData.append("template_name", templateName);
					formData.append("project_id", projectId);
					formData.append("odooId", odooId);
					formData.append("odooModel", selectedOdooModel);
					formData.append("odooInstallationName", odooInstallationName);
					formData.append("odooParentName", odooParentName);
					if (solarGradeData) {
						formData.append("solar_visit_id", solarGradeData.solar_visit_id);
						formData.append("solar_project_id", solarGradeData.solar_project_id);
						formData.append("solar_issue_id", solarGradeData.solar_issue_id);
					}
					let requestBlob = await fetch(api + "/project/image", {
						method: "POST",
						headers: {
							enctype: "multipart/form-data",
							Accept: "application/json",
							Authorization: "Bearer " + token,
						},
						body: formData,
						mode: "cors",
					});
					let resJson = await requestBlob.json().catch(() => ({}));
					if (!requestBlob.ok || !resJson.s3Id) {
						const failureReason = describeUploadFailure(resJson.message || resJson.msg);
						if (!failureReasons.includes(failureReason)) {
							failureReasons.push(failureReason);
						}
						continue;
					}
					setLastModifiedOn(new Date());
					setInSQS((prev) => [...prev, { id: resJson.s3Id, img: uploadImage }]);
				}
			} catch (error) {
				if (!failureReasons.includes("an unsupported image type")) {
					failureReasons.push("an unsupported image type");
				}
			} finally {
				setUploadingImages(false);
				setUploadedImages([]);
				if (uploadMulitpleRef.current) {
					uploadMulitpleRef.current.value = null;
				}
			}
			if (failureReasons.length > 0) {
				setUploadErrorMessage(failureReasons.join("; "));
			}
		},

		[
			uploadPreviews,
			fieldName,
			projectName,
			sectionName,
			templateTypeId,
			templateName,
			projectId,
			odooId,
			selectedOdooModel,
			odooInstallationName,
			odooParentName,
			solarGradeData,
			api,
			token,
			setLastModifiedOn,
			setInSQS,
		]
	);

	const handleDrag = (e) => {
		e.preventDefault();
		e.stopPropagation();

		if (e.type === "dragenter" || e.type === "dragover") {
			setDragActive(true);
		} else if (e.type === "dragleave") {
			setDragActive(false);
		}
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		const files = e.dataTransfer.files;
		const imagesArray = [];
		for (let i = 0; i < files.length; i++) {
			if (!files[i].type.match("image")) {
				continue;
			} else {
				imagesArray.push(files[i]);
				setUploadedImages(imagesArray);
			}
		}
	};

	const hasAiReviewPrompt = typeof aiReviewPrompt === "string" && aiReviewPrompt.trim() !== "";
	const imageQuery = sectionName.replaceAll("/", "-") + " - " + fieldName;
	const dropboxPhotoPath =
		templateTypeId === 1
			? "/In-Progress2/" +
			  projectName
					.trim()
					.split("/")
					.reduce((accumulator, curretValue, currentIndex, array) => accumulator.trim() + "/" + curretValue.trim()) +
			  "/Installation/Photos/" +
			  templateName
			: "/commercial/" +
			  projectName
					.split("-")
					.reduce((accumulator, currentValue, currentIndex, array) =>
						currentIndex !== projectName.split("-").length - 1 ? accumulator + "-" + currentValue : accumulator
					)
					.trim()
					.split("/")
					.reduce((accumulator, curretValue, currentIndex, array) => accumulator.trim() + "/" + curretValue.trim()) +
			  "/Installation/Photos/" +
			  templateName;

	return (
		<div>
			{showAiReviewModal ? (
				<AiImageReviewModal
					api={api}
					token={token}
					prompt={aiReviewPrompt}
					fieldValue={fieldValue}
					comment={fieldComment}
					imageQuery={imageQuery}
					imagePath={dropboxPhotoPath}
					initialThumbnailPaths={initialThumbnailPathes}
					knownPhotoPaths={knownPhotoPaths}
					projectId={projectId}
					fieldId={cameraid}
					onClose={() => setShowAiReviewModal(false)}
				/>
			) : null}
			{readOnly === false ? (
				<div className={dragActive ? "text-blue-500 bg-blue-100 animate-bounce" : null} onDragEnter={handleDrag}>
					<div className="flex gap-3 items-center">
						{windowWidth.current < 762 ? (
							<form className="flex">
								<label htmlFor={"pictureButton" + cameraid} className="cursor-pointer">
									<BsFillCameraFill className="text-5xl" />
								</label>
								<input
									ref={uploadSingleRef}
									className="hidden"
									type="file"
									id={"pictureButton" + cameraid}
									accept="image/*,application/pdf"
									onChange={(e) => {
										if (imgSrc) {
											URL.revokeObjectURL(imgSrc);
										}
										setImgSrc(
											URL.createObjectURL(
												new Blob([e.target.files[0]], {
													type: "image/jpeg",
													"Content-Disposition": "attachment",
												})
											)
										);
									}}
								/>
							</form>
						) : null}
						{imgSrc ? (
							<SaveImagePrompt
								cameraid={cameraid}
								fieldName={fieldName}
								saveName={sectionName.replaceAll(("/", "-") + " - " + fieldName + `(${numberOfUploaded})`)}
								uploadSingleRef={uploadSingleRef}
								imgSrc={imgSrc}
								uploadImage={uploadImage}
								setImgSrc={setImgSrc}
							/>
						) : null}
						{uploadPreviews && uploadPreviews.length > 0 ? (
							<SaveMultiplePrompt
								cameraid={cameraid}
								uploadPreviews={uploadPreviews}
								setUploadedImages={setUploadedImages}
								uploadImages={uploadImages}
								uploadMulitpleRef={uploadMulitpleRef}
								uploadedImages={uploadedImages}
							/>
						) : null}

						<form className="flex">
							<label htmlFor={"uploadButton" + cameraid}>
								<BsFillCloudArrowUpFill className="text-5xl cursor-pointer" />
							</label>
							<input
								ref={uploadMulitpleRef}
								type="file"
								id={"uploadButton" + cameraid}
								className="hidden"
								accept="image/*,application/pdf"
								onChange={(e) => setUploadedImages(e.target.files)}
								multiple
							/>
						</form>
						{hasAiReviewPrompt ? (
							<button
								type="button"
								className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white"
								onClick={() => setShowAiReviewModal(true)}>
								Review images
							</button>
						) : null}
						{dragActive && (
							<div
								className="absolute w-full h-full rounded-2xl"
								onDragEnter={handleDrag}
								onDragLeave={handleDrag}
								onDragOver={handleDrag}
								onDrop={handleDrop}></div>
						)}
					</div>
				</div>
			) : hasAiReviewPrompt ? (
				<button
					type="button"
					className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white"
					onClick={() => setShowAiReviewModal(true)}>
					Review images
				</button>
			) : null}
			<div ref={thumbnailBoxRef} className="flex flex-wrap mt-2 gap-2">
				<UploadedImages
					readOnly={readOnly}
					uploadingImages={uploadingImages}
					setInitialFieldData={setInitialFieldData}
					viewedThumbnailList={viewedThumbnailList}
					fieldName={fieldName}
					setNumberOfUploaded={setNumberOfUploaded}
					recentlyAddedPaths={recentlyAddedPaths}
					setRecentlyAddedPaths={setRecentlyAddedPaths}
					api={api}
					token={token}
					query={imageQuery}
					path={dropboxPhotoPath}
					stage={stage}
					initialThumbnailPathes={initialThumbnailPathes}
					projectId={projectId}
					setLastModifiedOn={setLastModifiedOn}
					uploadEditedImage={uploadEditedImage}
				/>
				{inSQS && inSQS.length > 0 ? <ImagesInSQS inSQS={inSQS} setInSQS={setInSQS} setRecentlyAddedPaths={setRecentlyAddedPaths} /> : null}
			</div>
			{uploadErrorMessage ? (
				<div className="flex items-center gap-2 bg-rose-100 p-2 mt-2 rounded-lg">
					<BsExclamationCircleFill color="red" className="text-3xl" />
					<p>One or more photos failed to upload: {uploadErrorMessage}</p>
				</div>
			) : null}
		</div>
	);
};

export default CameraContainer;
